<template>
  <div class="customer-stat-page q-pa-md">
    <div class="page-header q-mb-md">
      <div class="text-h6 text-primary">Customer Data</div>
      <div class="text-caption text-grey">
        {{ sortedCustomers.length }} of {{ customers.length }} customers
      </div>
    </div>

    <!-- Search & Sort Controls -->
    <div class="row q-mb-md q-col-gutter-md items-center">
      <div class="col-12 col-sm-6">
        <q-input
          outlined
          dense
          v-model="searchQuery"
          placeholder="Search by name, phone, email..."
          clearable
        >
          <template v-slot:prepend>
            <q-icon name="search" />
          </template>
        </q-input>
      </div>
      <div class="col-12 col-sm-3">
        <q-select
          outlined
          dense
          v-model="sortBy"
          :options="sortOptions"
          label="Sort by"
          emit-value
          map-options
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="row justify-center items-center q-py-xl">
      <q-spinner color="primary" size="40px" />
      <span class="q-ml-md text-grey">Loading customers...</span>
    </div>

    <!-- Customer List -->
    <div v-else class="customer-list">
      <div
        v-for="customer in sortedCustomers"
        :key="customer.id"
        class="customer-card"
        @click="onViewDetail(customer)"
      >
        <div class="row items-center q-col-gutter-md">
          <!-- Customer Info -->
          <div class="col-12 col-sm-5">
            <div class="customer-name">{{ customer.firstname }} {{ customer.lastname }}</div>
            <div class="customer-contact">
              <span v-if="customer.phone_number">{{ customer.phone_number }}</span>
              <span v-if="customer.phone_number && customer.email"> | </span>
              <span v-if="customer.email">{{ customer.email }}</span>
            </div>
          </div>

          <!-- Stats -->
          <div class="col-6 col-sm-3 text-center">
            <div class="stat-label">Total Purchase</div>
            <div class="stat-value">{{ customer.totalSaleVolume | formattedPrice }}</div>
          </div>

          <div class="col-6 col-sm-2 text-center">
            <div class="stat-label">Orders</div>
            <div class="stat-value">{{ customer.totalOrder }}</div>
          </div>

          <!-- Arrow -->
          <div class="col-12 col-sm-2 text-right">
            <q-icon name="chevron_right" size="24px" color="grey" />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "CustomerStat",
  data() {
    return {
      searchQuery: "",
      sortBy: "totalSaleVolume_desc",
      sortOptions: [
        { label: "Sales (High to Low)", value: "totalSaleVolume_desc" },
        { label: "Sales (Low to High)", value: "totalSaleVolume_asc" },
        { label: "Orders (High to Low)", value: "totalOrder_desc" },
        { label: "Orders (Low to High)", value: "totalOrder_asc" },
        { label: "Name (A-Z)", value: "name_asc" },
        { label: "Name (Z-A)", value: "name_desc" },
      ],
    };
  },
  computed: {
    ...mapGetters("customerStat", {
      customers: "getCustomers",
      loading: "isLoading",
    }),
    filteredCustomers() {
      if (!this.searchQuery || this.searchQuery.trim() === "") {
        return this.customers;
      }

      const query = this.searchQuery.toLowerCase().trim();
      return this.customers.filter((customer) => {
        const name = `${customer.firstname} ${customer.lastname}`.toLowerCase();
        const phone = (customer.phone_number || "").toLowerCase();
        const email = (customer.email || "").toLowerCase();

        return name.includes(query) || phone.includes(query) || email.includes(query);
      });
    },
    sortedCustomers() {
      const sorted = [...this.filteredCustomers];
      const [field, direction] = this.sortBy.split("_");

      sorted.sort((a, b) => {
        let valA, valB;

        if (field === "name") {
          valA = `${a.firstname} ${a.lastname}`.toLowerCase();
          valB = `${b.firstname} ${b.lastname}`.toLowerCase();
        } else {
          valA = a[field] || 0;
          valB = b[field] || 0;
        }

        if (direction === "asc") {
          return valA > valB ? 1 : -1;
        } else {
          return valA < valB ? 1 : -1;
        }
      });

      return sorted;
    },
  },
  mounted() {
    this.fetchCustomers();
  },
  methods: {
    ...mapActions("customerStat", ["fetchCustomers"]),
    onViewDetail(customer) {
      this.$router.push({
        name: "CustomerDetail",
        params: { id: customer.id },
      });
    },
  },
};
</script>

<style lang="scss" scoped>

.customer-stat-page {
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 12px;
}

.customer-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.customer-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #2c2a74;
    background: #fafafa;
  }
}

.customer-name {
  font-size: 16px;
  font-weight: 600;
  color: #2c2a74;
}

.customer-contact {
  font-size: 13px;
  color: #666;
  margin-top: 4px;
}

.stat-label {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

</style>
